import { Button, Dialog } from '@today/ui-primitives'
import { type ReactElement, useId } from 'react'

/** Copy of {@link SessionSettingsView}; each omitted key falls back to English. */
export interface SessionSettingsLabels {
	title: string
	description: string
	logOutEverywhere: string
	confirmTitle: string
	confirmBody: string
	confirm: string
	cancel: string
	close: string
}

const DEFAULT_LABELS: SessionSettingsLabels = {
	title: 'Sessions',
	description: 'Sign out of TODAY on every browser and device, including this one.',
	logOutEverywhere: 'Log out everywhere',
	confirmTitle: 'Log out everywhere?',
	confirmBody: 'Every browser and device signed in to your account — including this one — will need to sign in again.',
	confirm: 'Log out everywhere',
	cancel: 'Cancel',
	close: 'Close',
}

/**
 * Props for {@link SessionSettingsView}.
 *
 * @property isConfirming - The confirmation dialog is open.
 * @property error - A server refusal; the user stays signed in.
 */
export interface SessionSettingsViewProps {
	isConfirming: boolean
	onRequest: () => void
	onConfirm: () => void
	onCancel: () => void
	isBusy?: boolean
	error?: string | null
	labels?: Partial<SessionSettingsLabels>
}

/** The Settings "Sessions" section: "Log out everywhere", always confirmed (organism, TO-197). */
export function SessionSettingsView(props: SessionSettingsViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	const titleId = useId()
	return (
		<section
			aria-labelledby={titleId}
			className="flex flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6"
		>
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col gap-1">
					<h2 id={titleId} className="text-ds-lg font-semibold text-ds-text">
						{text.title}
					</h2>
					<p className="text-ds-sm text-ds-text-muted">{text.description}</p>
				</div>
				<Button variant="danger" onPress={props.onRequest}>
					{text.logOutEverywhere}
				</Button>
			</header>
			{props.error && !props.isConfirming ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			<Dialog
				isOpen={props.isConfirming}
				onOpenChange={(open) => (open ? undefined : props.onCancel())}
				title={text.confirmTitle}
				labels={{ close: text.close }}
			>
				<div className="flex flex-col gap-3">
					{props.error ? (
						<p role="alert" className="text-ds-sm text-ds-danger">
							{props.error}
						</p>
					) : null}
					<p className="text-ds-sm text-ds-text">{text.confirmBody}</p>
					<div className="mt-1 flex gap-2">
						<Button variant="danger" isDisabled={props.isBusy} onPress={props.onConfirm}>
							{text.confirm}
						</Button>
						<Button variant="muted" onPress={props.onCancel}>
							{text.cancel}
						</Button>
					</div>
				</div>
			</Dialog>
		</section>
	)
}
